import { useSelector } from "react-redux";
import { selectCurrentUserId } from "newui/features/Auth/authSelectors";
import { PanelEvaluationView } from "./panel/PanelEvaluationView";
import { PanelMemberScoringView } from "./panel/member/PanelMemberScoringView";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { PanelMemberStatus, PanelMode } from "../../../types/panel.type";
import { EvaluationStepNumber, PanelMemberBlockedReason } from "../../../types/evaluation.type";
import { SoloEvaluationView } from "./components/SoloEvaluationView";
import { useEvaluationRfxId } from "../../../hooks/useEvaluationRfxId";

interface InitialEvaluationStepProps {
	currentStep: EvaluationStepNumber;
	disclaimerEnabled?: boolean;
	evalId?: number;
	// Visibility-only lead override forwarded from RFxEvaluationSteps. True when a
	// switched executive is viewing as RFx Lead (read-only). Used ONLY to reveal
	// the lead view; writes stay blocked by the read-only wrapper + backend policy.
	isRfxLeadView?: boolean;
	isLoading?: boolean;
	panelMemberId?: number;
	panelMemberStatus?: string;
	panelMode?: PanelMode | string | null;
	rfxData: Rfx;
	scoringBlockedReason?: PanelMemberBlockedReason | null;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onCompletionChange?: (isComplete: boolean) => void;
	onRefreshUserContext?: () => void;
}

export const InitialEvaluationStep: React.FC<InitialEvaluationStepProps> = ({
	currentStep,
	disclaimerEnabled = false,
	rfxData,
	evalId,
	isRfxLeadView = false,
	panelMode,
	panelMemberId,
	panelMemberStatus,
	scoringBlockedReason,
	isLoading = false,
	onBeforeNext,
	onCompletionChange,
	onRefreshUserContext,
}) => {
	// TASK-028: see `useEvaluationRfxId` — never derive this from `rfxData?.id`.
	const rfxId = useEvaluationRfxId(rfxData);
	const currentUserId = useSelector(selectCurrentUserId);
	const isPanel = panelMode === "manual_panel" || panelMode === "ai_panel";
	const isRfxLead = currentUserId !== null && rfxData?.rfxLead?.id === currentUserId;
	// Visibility-only override: a switched executive in read-only "View as RFx
	// Lead" mode sees the lead view. Writes stay blocked by the read-only wrapper
	// in EvaluationPage and the backend policy, so this only affects what renders.
	const effectiveIsRfxLead = isRfxLead || isRfxLeadView;

	// Panel mode: show panel-specific views
	if (isPanel && evalId) {
		if (effectiveIsRfxLead) {
			return <PanelEvaluationView rfxData={rfxData} evalId={evalId} isRfxLead={effectiveIsRfxLead} currentStep={currentStep} disclaimerEnabled={disclaimerEnabled} onCompletionChange={onCompletionChange} onRefreshUserContext={onRefreshUserContext} />;
		}

		if (panelMemberId && panelMemberStatus) {
			return (
				<PanelMemberScoringView
					currentStep={currentStep}
					rfxData={rfxData}
					evalId={evalId}
					memberId={panelMemberId}
					memberStatus={panelMemberStatus as PanelMemberStatus}
					scoringBlockedReason={scoringBlockedReason}
				/>
			);
		}
		// Non-lead, non-panel user in panel mode — show read-only via PanelEvaluationView
		return <PanelEvaluationView rfxData={rfxData} evalId={evalId} isRfxLead={false} />;
	}

	return (
		<SoloEvaluationView
			rfxId={rfxId}
			evalId={evalId}
			isLoading={isLoading}
			onBeforeNext={onBeforeNext}
			onCompletionChange={onCompletionChange}
		/>
	);
};
